import { useSyncExternalStore } from "react";

import {
	type BeforeInstallPromptEvent,
	clearDeferredPrompt,
	getInstallPromptSnapshot,
	subscribeInstallPrompt
} from "~/pwa/installPromptStore";
import isStandaloneDisplay from "~/pwa/isStandaloneDisplay";
import useStandaloneApp from "~/pwa/useStandaloneApp";

type InstallPlatform = "android" | "ios" | "unsupported";
type InstallOutcome = "accepted" | "dismissed" | "unavailable";

interface InstallPromptState {
	canInstall: boolean;
	platform: InstallPlatform;
	promptInstall: () => Promise<InstallOutcome>;
}

const isIosSafari = (): boolean => {
	if (typeof navigator === "undefined") return false;
	const ua = navigator.userAgent ?? "";
	const isIos = /iphone|ipad|ipod/i.test(ua);
	const isWebkit = /safari/i.test(ua) && !/crios|fxios|edgios|opios/i.test(ua);
	return isIos && isWebkit;
};

const useInstallPrompt = (): InstallPromptState => {
	const { isConfined } = useStandaloneApp();
	const { deferredPrompt, installed } = useSyncExternalStore(subscribeInstallPrompt, getInstallPromptSnapshot);

	const availablePrompt = isConfined ? null : deferredPrompt;
	const platform: InstallPlatform = availablePrompt != null ? "android" : isIosSafari() ? "ios" : "unsupported";

	const promptInstall = async (): Promise<InstallOutcome> => {
		if (availablePrompt == null) return "unavailable";
		await availablePrompt.prompt();
		const choice = await availablePrompt.userChoice;
		clearDeferredPrompt();
		return choice.outcome;
	};

	const canInstall =
		!isConfined && !isStandaloneDisplay() && !installed && (platform === "ios" || availablePrompt != null);

	return { canInstall, platform, promptInstall };
};

export type { BeforeInstallPromptEvent, InstallOutcome, InstallPlatform, InstallPromptState };
export default useInstallPrompt;
